import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSwipeToClose } from '@/hooks/useSwipeToClose';
import { cn } from '@/lib/utils';

interface BacklogPanelProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  /** Optional class for the panel container */
  className?: string;
}

/**
 * Fixed-position backlog panel rendered in-place (no portal).
 * Uses `right` transition instead of `transform` so the list container
 * has no transform and @hello-pangea/dnd drop coordinates stay correct.
 */
export function BacklogPanel({ open, onClose, children, className }: BacklogPanelProps) {
  const swipeToClose = useSwipeToClose(onClose, 'left');

  return (
    <>
      {/* Backdrop - no portal, same tree as DragDropContext */}
      <div
        aria-hidden
        className={cn(
          'fixed inset-0 z-40 bg-black/80 transition-opacity duration-300 ease-out',
          open ? 'opacity-100' : 'opacity-0 pointer-events-none'
        )}
        onClick={onClose}
      />
      {/* Panel - fixed, animate via right (not transform) so DnD list isn't in a transformed context */}
      <div
        role="dialog"
        aria-label="All Todos"
        className={cn(
          'fixed inset-y-0 z-50 w-80 sm:w-96 bg-background shadow-lg border-l',
          'transition-[right] duration-300 ease-out',
          'content-padding touch-pan-y',
          open ? 'right-0' : '-right-full pointer-events-none',
          className
        )}
        {...swipeToClose}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between pb-0 shrink-0 relative min-h-[44px]">
            <h2 className="sr-only">All Todos</h2>
            <Button
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-sm opacity-70 hover:opacity-100"
              onClick={onClose}
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex-1 min-h-0 pt-4 overflow-hidden flex flex-col">
            {children}
          </div>
        </div>
      </div>
    </>
  );
}
